import { useEffect, useState, useRef } from 'react';
import { IJobTitle, Location, ITalent, BrowserUploadFile, ITalentBasicInput } from '@bloomays-lib/types.shared';
import { Tabs, TabPanel, Modal } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import TalentSheetSideBar from './TalentSheetSideBar';
import TalentSheetExperience from './TalentSheetExperience';
import TalentSheetInResumed from './TalentSheetInResumed';

import debounce from 'lodash/debounce';
import {
  FIND_TALENT,
  GET_ADDRESSES,
  LIST_JOBS,
  UPSERT_CV,
  UPSERT_TALENT_BASIC,
} from '@bloomays-lib/adapter.api-talent';
import { cancel, notify } from '../../helpers/toastify';
import TalentSheetActivityLog from './TalentSheetActivityLog';
import TalentSheetCV from './TalentSheetCV';
import TalentSheetFormation from './TalentSheetFormation';
import ResponsiveActionMenu from './ResponsiveActionMenu';
import SyncTalentQualification from '../molecules/SyncTalentQualification';
import { getTalentActions } from '../../services/serviceTalentAction';
import { removeGqlTypename } from '@bloomays-lib/utils.shared';

export type TalentSheetProps = {
  id: string;
  origin: 'searchPage' | 'talentPage';
  handleOpenSyncTalent: () => void;
  openSyncTalent: boolean;
  handleCloseSyncTalent: () => void;
};

const TalentSheet = ({ id, origin, handleOpenSyncTalent, openSyncTalent, handleCloseSyncTalent }: TalentSheetProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  const [modalParam, setModalParam] = useState(searchParams.get('upsertQualif'));
  const [tabsValue, setTabsValue] = useState(2);
  const { t } = useTranslation(['talentSheet', 'random']);
  const [talent, setTalent] = useState<ITalent>();
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const [getAdress, { data: dataLocations, loading: loadingLocations }] = useLazyQuery<{ getAdresses: IJobTitle[] }>(
    GET_ADDRESSES,
    {
      context: { clientName: 'api.talents' },
    },
  );

  const [getTalent, { data, error }] = useLazyQuery<{ findTalent: ITalent }>(FIND_TALENT, {
    variables: {
      id: id,
    },
    context: { clientName: 'api.talents' },
  });

  useEffect(() => {
    if (error) {
      notify('error', 'Impossible de charger le talent', error);
    }
  }, [error]);

  const { data: dataJobTitle } = useQuery<{
    listJobTitles: IJobTitle[];
  }>(LIST_JOBS, {
    variables: {
      iso: true,
    },
    context: { clientName: 'api.talents' },
  });

  const [upsertBasic, { loading: loadingUpsertBasic }] = useMutation<{
    upsertTalentBasic: ITalent;
  }>(UPSERT_TALENT_BASIC, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
    onCompleted: () => {
      setUpdatedAt(new Date());
    },
  });

  const managedUpdateBasic = async (talentBasic: ITalentBasicInput) => {
    const res = await upsertBasic({
      variables: {
        input: talentBasic,
      },
    });
    return res.data?.upsertTalentBasic;
  };

  const [upsertCVFile] = useMutation<{
    uploadCVFile: { id: string; cv?: string };
  }>(UPSERT_CV, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
    onCompleted: () => {
      setUpdatedAt(new Date());
    },
  });
  const [locationsState, setLocationsState] = useState<Location[] | undefined>(dataLocations?.getAdresses);
  const CVRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (id) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getTalent({
        variables: { id: id },
        context: { clientName: 'api.talents' },
      });
    }
  }, [getTalent, id]);

  useEffect(() => {
    if (data?.findTalent) {
      setTalent(removeGqlTypename(data.findTalent));
    }
  }, [data]);

  const uploadCv = async (files: BrowserUploadFile[]) => {
    if (!files[0]) {
      return;
    }
    try {
      notify('info', `${t('updateInProgress')}`, null, {
        hideProgressBar: false,
        autoClose: 30000,
      });
      const upsertCV = await upsertCVFile({
        variables: {
          input: {
            id: id,
            file: files[0],
          },
        },
      });
      cancel();
      upsertCV?.data?.uploadCVFile && upsertCV?.data?.uploadCVFile?.cv
        ? notify('success', 'Le CV a bien été chargé ! :)')
        : notify('error', "Le CV n'a bien été chargé ! 😓");
    } catch (e: any) {
      cancel();
      notify('error', `Le CV n'a bien été chargé ! 😓 ${e.message}`);
    }
  };

  const getLocationFromMaps = async (locationSearched: string) => {
    await getAdress({
      variables: { input: locationSearched.toLocaleLowerCase() },
      context: { clientName: 'api.talents' },
    });
  };

  const getJobsDebounced = debounce(getLocationFromMaps, 500);

  const onSearchLocation = async (e: any) => {
    if (e.target.value.length >= 3) {
      await getJobsDebounced(e.target.value);
    }

    if (e.target.value.length === 0) {
      setLocationsState([]);
    }
  };

  useEffect(() => {
    setLocationsState(dataLocations?.getAdresses);
  }, [dataLocations]);

  const actions = talent ? getTalentActions(talent, origin, handleOpenSyncTalent, t) : [];

  return (
    <StyledContainer>
      {
        <Modal
          additionnalCSS={{
            overflowY: 'auto',
            width: '65%',
            padding: '32px',
            border: 'none',
            borderRadius: '20px',
          }}
          open={openSyncTalent || modalParam === 'active'}
          onClose={() => {
            handleCloseSyncTalent();
            setModalParam('unactive');
          }}
        >
          {talent && talent.id && talent.email ? (
            <SyncTalentQualification
              handleClose={() => {
                handleCloseSyncTalent();
                setModalParam('unactive');
              }}
              talent={talent}
            />
          ) : (
            <>{t('emailMissingForSync')}</>
          )}
        </Modal>
      }
      <TalentSheetSideBar
        origin={origin}
        talent={talent}
        loadingBasic={loadingUpsertBasic}
        upsertBasic={managedUpdateBasic}
        jobTitleList={dataJobTitle?.listJobTitles}
        loadingLocation={loadingLocations}
        onSearch={onSearchLocation}
        locationsState={locationsState}
        setLocationsState={setLocationsState}
      />
      <Tabs
        variant="menu"
        titles={[
          t('inResumed', { ns: 'talentSheet' }),
          'CV',
          t('xp', { ns: 'talentSheet' }),
          t('school', { ns: 'talentSheet' }),
          t('history', { ns: 'talentSheet' }),
        ]}
        value={tabsValue}
        setValue={setTabsValue}
      >
        <TabPanel value={tabsValue} index={0}>
          <TalentSheetInResumed talent={talent} loadingUpsert={loadingUpsertBasic} upsertBasic={managedUpdateBasic} />
        </TabPanel>
        <TabPanel value={tabsValue} index={1}>
          <TalentSheetCV talent={talent} uploadCv={uploadCv} CVRef={CVRef} />
        </TabPanel>
        <TabPanel value={tabsValue} index={2}>
          <TalentSheetExperience talent={talent} />
        </TabPanel>
        <TabPanel value={tabsValue} index={3}>
          <TalentSheetFormation talent={talent} />
        </TabPanel>
        <TabPanel value={tabsValue} index={4}>
          <TalentSheetActivityLog talentId={id} updatedAt={updatedAt} />
        </TabPanel>
      </Tabs>
      <StyledAction>
        <ResponsiveActionMenu actions={actions} mobileBreakpoint={1200} />
      </StyledAction>
    </StyledContainer>
  );
};

export default TalentSheet;
export { TalentSheet };

const StyledAction = styled('div')(
  () => `
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 18px;
  position: absolute;
  zIndex: 2;
  right: 0;
  padding-top: 26px;
  padding-right: 24px;
`,
);

const StyledContainer = styled('div')(
  () => `
  display: flex;
  flex-flow: row nowrap;
  height: 100%;
  `,
);
